import { useMemo } from 'react';
import { useWarrantyDetails } from '@/features/warranties/hooks/useWarrantyDetails';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { extractMonthlyFees, calculateWarrantyPricing } from '@/features/warranties/services/pricingCalculator';
import type { ContractFormState } from './useContractFormWizard';

const TVA = 0.2;

export interface PricingSummary {
  totalPriceTTC: number;
  totalPriceHT: number;
  servicePriceHT: number;
  platformFeePercentage: number;
  platformFeesLabel: string;
  totalPlatformFeesHT: number;
  isPaidByCustomer: boolean;
  displayedPlatformFeesAmount: number;
  netRevenueHT: number;
  netSellerHT: number;
  headTransferPrice: number;
  memberTransferPrice: number;
  monthlyPriceWithFees: number;
  monthlyFeesPerMonthTTC: number;
  monthlyPriceForClient: number;
  monthlyRevenueForSeller: number;
  commissionAmount: number;
  pricingDisplayLabel: string;
  pricingDisplayValue: number;
}

export interface PriceInfo {
  label: string;
  feesMessage: string | null;
  clientPaysMonthly: number | null;
  sellerReceivesMonthly: number | null;
  feePercentage: number | null;
  feeAmountTTC: number | null;
}

export interface PriceFieldConfig {
  useMonthlyInput: boolean;
  field: 'sellingPrice' | 'monthlyPrice' | 'monthlyPriceWithFees';
  displayReadOnly: boolean;
}

export function usePricingInfo(formData: ContractFormState) {
  const { data: warranty } = useWarrantyDetails(formData.warrantyUuid);
  const { data: config } = useEffectiveConfiguration(formData.societyId);
  const { data: society } = useSociety(formData.societyId);

  const pricingSummary = useMemo((): PricingSummary | null => {
    if (!warranty || !config || !society) return null;

    const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
    const isBothOption = warranty.isCashPayment && warranty.isMensualisable;

    const monthlyPrice = Number(formData.monthlyPrice) || 0;
    const duration = warranty.duration;
    const totalPriceTTC = isMonthlyOnly
      ? monthlyPrice * duration
      : Number(formData.sellingPrice) || 0;
    const totalPriceHT = totalPriceTTC / (1 + TVA);
    const servicePriceHT = warranty.servicePrice;

    // Rétrocession / facturation uniquement si la config effective de la société active l'option
    const isTransferPrice = !!config.isTransferPrice;
    const pricingInfo = calculateWarrantyPricing(warranty, society, formData.includedOption, isTransferPrice);

    const effectiveHeadTransferPrice = !isTransferPrice ? 0
      : formData.includedOption ? pricingInfo.headTransferPrice || 0 : Number(formData.headTransferPrice) || 0;
    const effectiveMemberTransferPrice = !isTransferPrice ? 0
      : formData.includedOption ? pricingInfo.memberTransferPrice || 0 : Number(formData.memberTransferPrice) || 0;

    let platformFeePercentage = 0;
    let platformFeesLabel = '';
    let isPaidByCustomer = false;
    let monthlyPriceWithFees = 0;
    let monthlyFeesPerMonthTTC = 0;
    let monthlyPriceForClient = 0;
    let monthlyRevenueForSeller = 0;

    if (isMonthlyOnly) {
      platformFeePercentage = config.monthlyPlateformFees || 0;
      platformFeesLabel = 'Frais de service';
      isPaidByCustomer = false;
      monthlyFeesPerMonthTTC = extractMonthlyFees(monthlyPrice, platformFeePercentage);
      monthlyPriceForClient = monthlyPrice;
      monthlyRevenueForSeller = monthlyPrice - monthlyFeesPerMonthTTC;
    } else if (isBothOption && formData.paymentType === 2) {
      platformFeePercentage = config.monthlyPlateformFees || 0;
      platformFeesLabel = 'Frais de service';
      isPaidByCustomer = true;
      monthlyPriceWithFees = Number(formData.monthlyPriceWithFees) || 0;
      const monthlyBaseTTC = monthlyPriceWithFees * (1 - platformFeePercentage / 100);
      monthlyFeesPerMonthTTC = monthlyPriceWithFees - monthlyBaseTTC;
      monthlyPriceForClient = monthlyPriceWithFees;
      monthlyRevenueForSeller = monthlyBaseTTC;
    }

    const headTransferPrice = effectiveHeadTransferPrice;
    const memberTransferPrice = effectiveMemberTransferPrice;

    const totalPlatformFeesHT = (monthlyFeesPerMonthTTC * duration) / (1 + TVA);
    const netRevenueHT = totalPriceHT - servicePriceHT - headTransferPrice + memberTransferPrice - totalPlatformFeesHT;

    const commission = Number(formData.commission) || 0;
    const commissionType = formData.commissionType;

    let commissionAmount = 0;
    if (commission > 0) {
      if (commissionType === 1) {
        commissionAmount = commission;
      } else if (commissionType === 2) {
        commissionAmount = (totalPriceHT * commission) / 100;
      }
    }

    const netSellerHT = netRevenueHT - commissionAmount;

    return {
      totalPriceTTC,
      totalPriceHT,
      servicePriceHT,
      platformFeePercentage,
      platformFeesLabel,
      totalPlatformFeesHT,
      isPaidByCustomer,
      displayedPlatformFeesAmount: totalPlatformFeesHT,
      netRevenueHT,
      netSellerHT,
      headTransferPrice,
      memberTransferPrice,
      monthlyPriceWithFees,
      monthlyFeesPerMonthTTC,
      monthlyPriceForClient,
      monthlyRevenueForSeller,
      commissionAmount,
      pricingDisplayLabel: pricingInfo.displayLabel,
      pricingDisplayValue: pricingInfo.displayValue,
    };
  }, [warranty, config, society, formData]);

  const priceInfo = useMemo((): PriceInfo => {
    const defaultInfo: PriceInfo = { label: 'Prix de vente TTC', feesMessage: null, clientPaysMonthly: null, sellerReceivesMonthly: null, feePercentage: null, feeAmountTTC: null };
    if (!warranty || !config || !pricingSummary || formData.includedOption) {
      return defaultInfo;
    }

    const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
    const isCashOnly = warranty.isCashPayment && !warranty.isMensualisable;
    const isBoth = warranty.isCashPayment && warranty.isMensualisable;
    const hasOnlinePayment = config.onlinePayment === 2;

    if (isMonthlyOnly) {
      const monthlyFeePercentage = config.monthlyPlateformFees || 0;
      const monthlyFeeTTC = pricingSummary.monthlyFeesPerMonthTTC || 0;
      return {
        label: 'Prix mensuel client TTC',
        feesMessage: `dont ${monthlyFeeTTC.toFixed(2)} € /TTC (${monthlyFeePercentage}%) de frais inclus par mois`,
        clientPaysMonthly: pricingSummary.monthlyPriceForClient,
        sellerReceivesMonthly: pricingSummary.monthlyRevenueForSeller,
        feePercentage: monthlyFeePercentage,
        feeAmountTTC: monthlyFeeTTC,
      };
    }

    if (isBoth) {
      if (formData.paymentType === 2) {
        const monthlyFeePercentage = config.monthlyPlateformFees || 0;
        const monthlyFeeTTC = pricingSummary.monthlyFeesPerMonthTTC || 0;
        return {
          label: 'Prix mensuel client TTC',
          feesMessage: `dont ${monthlyFeeTTC.toFixed(2)} € /TTC (${monthlyFeePercentage}%) de frais inclus par mois`,
          clientPaysMonthly: pricingSummary.monthlyPriceForClient,
          sellerReceivesMonthly: pricingSummary.monthlyRevenueForSeller,
          feePercentage: monthlyFeePercentage,
          feeAmountTTC: monthlyFeeTTC,
        };
      } else {
        if (hasOnlinePayment) {
          const onlineFeePercentage = config.onlinePlateformFees || 0;
          const totalPriceHT = pricingSummary.totalPriceHT;
          const onlineFeeTTC = ((totalPriceHT * onlineFeePercentage) / 100) * (1 + TVA);
          return {
            label: 'Prix de vente TTC',
            feesMessage: `dont ${onlineFeeTTC.toFixed(2)} € /TTC (${onlineFeePercentage}%) de frais inclus`,
            clientPaysMonthly: null,
            sellerReceivesMonthly: null,
            feePercentage: onlineFeePercentage,
            feeAmountTTC: onlineFeeTTC,
          };
        }
        return defaultInfo;
      }
    }

    if (isCashOnly) {
      if (hasOnlinePayment) {
        const onlineFeePercentage = config.onlinePlateformFees || 0;
        const totalPriceHT = pricingSummary.totalPriceHT;
        const onlineFeeTTC = ((totalPriceHT * onlineFeePercentage) / 100) * (1 + TVA);
        return {
          label: 'Prix de vente TTC',
          feesMessage: `dont ${onlineFeeTTC.toFixed(2)} € /TTC (${onlineFeePercentage}%) de frais inclus`,
          clientPaysMonthly: null,
          sellerReceivesMonthly: null,
          feePercentage: onlineFeePercentage,
          feeAmountTTC: onlineFeeTTC,
        };
      }
      return defaultInfo;
    }

    return defaultInfo;
  }, [warranty, config, formData.paymentType, formData.includedOption, pricingSummary]);

  const priceFieldConfig = useMemo((): PriceFieldConfig => {
    if (!warranty || !pricingSummary) return { useMonthlyInput: false, field: 'sellingPrice', displayReadOnly: false };

    const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
    const isBoth = warranty.isCashPayment && warranty.isMensualisable;

    if (isMonthlyOnly) {
      return { useMonthlyInput: true, field: 'monthlyPrice' as const, displayReadOnly: false };
    }

    if (isBoth && formData.paymentType === 2) {
      return { useMonthlyInput: true, field: 'monthlyPriceWithFees' as const, displayReadOnly: false };
    }

    return { useMonthlyInput: false, field: 'sellingPrice' as const, displayReadOnly: false };
  }, [warranty, formData.paymentType, pricingSummary]);

  return { warranty, config, society, pricingSummary, priceInfo, priceFieldConfig };
}
